import { z } from "zod";
import type { ESignature } from "./charts";

/** What can go in a staff contract's wording, in {{double braces}}; filled in for each person when it's sent. */
export const CONTRACT_PLACEHOLDERS = [
  { key: "clinic.name", label: "Clinic name", example: "Vaulted Vitality" },
  { key: "clinic.address", label: "Clinic address", example: "249 NW 71st St., Oklahoma City, OK 73116" },
  { key: "staff.name", label: "Their full name", example: "Annalyssa Smith" },
  { key: "staff.first_name", label: "Their first name", example: "Annalyssa" },
  { key: "staff.title", label: "Their credentials or title", example: "RN BSN" },
  { key: "staff.email", label: "Their email", example: "annalyssa@example.com" },
  { key: "staff.phone", label: "Their phone", example: "(405) 555-0101" },
  { key: "staff.address", label: "Their address (from their W-9)", example: "1 Main St, Oklahoma City, OK 73116" },
  { key: "pay.commission", label: "Their commission", example: "50%" },
  { key: "pay.overhead", label: "Overhead taken out first", example: "100%" },
  { key: "pay.overhead_minimum", label: "Minimum overhead", example: "10%" },
  { key: "pay.tips", label: "Their share of tips", example: "100%" },
  { key: "start_date", label: "Start date (picked when sending)", example: "November 15, 2026" },
  { key: "today", label: "Date it's sent", example: "November 4, 2026" },
  { key: "signer.name", label: "Clinic signer's name", example: "Ryan Etsell" },
  { key: "signer.title", label: "Clinic signer's title", example: "Owner" },
] as const;
export type ContractPlaceholder = (typeof CONTRACT_PLACEHOLDERS)[number]["key"];

const TOKEN = /\{\{\s*([a-z_.]+)\s*\}\}/gi;
const KNOWN = new Set<string>(CONTRACT_PLACEHOLDERS.map((p) => p.key));

/** Placeholders in the wording that aren't real ones ("{{staf.name}}"). */
export const unknownPlaceholders = (body: string) => [...new Set([...body.matchAll(TOKEN)].map((m) => m[1]!.toLowerCase()).filter((k) => !KNOWN.has(k)))];

/** The wording filled in; `missing` = placeholders used with no value for this person (left as a blank line to fill by hand). */
export function fillContract(body: string, values: Partial<Record<ContractPlaceholder, string>>): { text: string; missing: ContractPlaceholder[]; unknown: string[] } {
  const missing = new Set<ContractPlaceholder>();
  const text = body.replace(TOKEN, (whole, raw: string) => {
    const k = raw.toLowerCase();
    if (!KNOWN.has(k)) return whole;
    const v = values[k as ContractPlaceholder];
    if (v) return v;
    missing.add(k as ContractPlaceholder);
    return "________";
  });
  return { text, missing: [...missing], unknown: unknownPlaceholders(body) };
}

/** The wording as blocks: a line starting "#" is a heading, "-" or "•" a bullet, blank lines split paragraphs. */
export type ContractBlock = { kind: "heading" | "paragraph" | "bullet"; text: string };
export function contractBlocks(text: string): ContractBlock[] {
  const out: ContractBlock[] = [];
  let para: string[] = [];
  const flush = () => {
    if (para.length) out.push({ kind: "paragraph", text: para.join(" ") });
    para = [];
  };
  for (const raw of text.replace(/\r\n?/g, "\n").split("\n")) {
    const line = raw.trim();
    if (!line) flush();
    else if (/^#+\s*/.test(line)) {
      flush();
      out.push({ kind: "heading", text: line.replace(/^#+\s*/, "") });
    } else if (/^[-•*]\s+/.test(line)) {
      flush();
      out.push({ kind: "bullet", text: line.replace(/^[-•*]\s+/, "") });
    } else para.push(line);
  }
  flush();
  return out;
}

export const contractTemplateSchema = z.object({
  name: z.string().trim().min(1, "Name it").max(120),
  body: z.string().max(100_000).default(""),
});

export const sendContractSchema = z.object({
  templateId: z.string().uuid(),
  startDate: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .nullable()
    .default(null),
  signerStaffId: z.string().uuid(),
});

export const CONTRACT_SIGN_STATEMENT = "I have read this agreement and agree to its terms, and I sign it electronically.";
export const CONTRACT_COUNTERSIGN_STATEMENT = "I sign this agreement electronically on behalf of the clinic.";

export interface ContractTemplateView {
  id: string;
  name: string;
  body: string;
  version: number;
  unknown: string[];
  updatedAt: string;
  archived: boolean;
}

export type ContractStatus = "sent" | "staff_signed" | "signed" | "replaced" | "cancelled";
export const CONTRACT_STATUS_LABELS: Record<ContractStatus, string> = {
  sent: "Waiting for them to sign",
  staff_signed: "Waiting for the clinic to sign",
  signed: "Fully signed",
  replaced: "Replaced",
  cancelled: "Cancelled",
};

export interface StaffContractView {
  id: string;
  staffMemberId: string;
  staffName: string;
  title: string;
  templateVersion: number;
  startDate: string | null;
  body: string;
  status: ContractStatus;
  signerStaffId: string;
  signerName: string;
  sentAt: string;
  sentByName: string | null;
  staffSignature: ESignature | null;
  clinicSignature: ESignature | null;
  signedAt: string | null;
  hasPdf: boolean;
}
